// Manual reservation entry — for phone/walk-in bookings staff take offline.
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { adminApi, formatApiError } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "mt-1 w-full border border-[#0b1f22]/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold bg-white";

export default function AdminReservationNew() {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        guest_name: "", email: "", phone: "", country: "",
        adults: 2, children: 0,
        check_in: "", check_out: "",
        room_slug: "", room_quantity: 1, rate_kind: "flexible",
        subtotal: "", discount_amount: "0", discount_reason: "",
        taxes_fees: "0", total: "",
        source: "phone", special_requests: "", staff_note: "",
    });

    useEffect(() => {
        axios.get(`${API}/rooms`).then((r) => setRooms(r.data)).catch(() => {});
    }, []);

    // Auto-fill subtotal when room + dates are set
    useEffect(() => {
        const room = rooms.find((r) => r.slug === form.room_slug);
        if (!room || !form.check_in || !form.check_out) return;
        try {
            const nights = Math.max(1, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000));
            const sub = Number(room.price || 0) * nights * Math.max(1, Number(form.room_quantity || 1));
            const disc = Number(form.discount_amount || 0);
            const tax = Number(form.taxes_fees || 0);
            setForm((f) => ({ ...f, subtotal: sub, total: sub - disc + tax }));
        } catch { /* noop */ }
        // eslint-disable-next-line
    }, [form.room_slug, form.check_in, form.check_out, form.room_quantity]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const num = (v) => (v === "" || v == null ? 0 : Number(v));
            const payload = {
                ...form,
                adults: num(form.adults), children: num(form.children), room_quantity: num(form.room_quantity),
                subtotal: num(form.subtotal), discount_amount: num(form.discount_amount),
                taxes_fees: num(form.taxes_fees), total: num(form.total),
                status: "confirmed",
            };
            const { data } = await adminApi.post("/admin/reservations", payload);
            toast.success(`Created ${data.confirmation_number}`);
            navigate(`/admin/reservations/${data.id}`, { replace: true });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div className="pb-16">
            <div className="flex items-center justify-between mb-6">
                <Link to="/admin/reservations" className="text-[11px] uppercase tracking-[0.2em] text-[#0b1f22]/60 hover:text-gold inline-flex items-center gap-1">
                    <ArrowLeft size={12} /> Back to reservations
                </Link>
            </div>
            <header className="mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">New reservation</div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Manual booking</h1>
                <p className="text-[#0b1f22]/60 mt-2 text-sm">Use this for phone bookings, walk-ins, and any reservation not made through the public site.</p>
            </header>

            <form onSubmit={submit} className="bg-white border border-[#0b1f22]/10 rounded-2xl p-6 lg:p-8 space-y-6" data-testid="new-reservation-form">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-3">Guest</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Full name *</span>
                            <input required className={inputCls} value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="new-guest-name" />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Email *</span>
                            <input type="email" required className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Phone</span>
                            <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Country</span>
                            <input className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                        </label>
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-3">Stay</div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Check-in *</span>
                            <input type="date" required className={inputCls} value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Check-out *</span>
                            <input type="date" required className={inputCls} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Room *</span>
                            <select required className={inputCls} value={form.room_slug} onChange={(e) => setForm({ ...form, room_slug: e.target.value })} data-testid="new-room">
                                <option value="">Select…</option>
                                {rooms.map((r) => <option key={r.slug} value={r.slug}>{r.name} · ${r.price}/nt</option>)}
                            </select>
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Room quantity</span>
                            <input type="number" min="1" className={inputCls} value={form.room_quantity} onChange={(e) => setForm({ ...form, room_quantity: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Adults</span>
                            <input type="number" min="1" className={inputCls} value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Children</span>
                            <input type="number" min="0" className={inputCls} value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} />
                        </label>
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-3">Amounts (USD) — auto-filled when you pick a room, adjust as needed</div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Subtotal</span>
                            <input type="number" step="0.01" className={inputCls} value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Discount</span>
                            <input type="number" step="0.01" className={inputCls} value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Taxes & fees</span>
                            <input type="number" step="0.01" className={inputCls} value={form.taxes_fees} onChange={(e) => setForm({ ...form, taxes_fees: e.target.value })} />
                        </label>
                        <label className="block md:col-span-2"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Discount reason</span>
                            <input className={inputCls} value={form.discount_reason} onChange={(e) => setForm({ ...form, discount_reason: e.target.value })} />
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Total</span>
                            <input type="number" step="0.01" className={inputCls} value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} />
                        </label>
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55 mb-3">Meta</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Source</span>
                            <select className={inputCls} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                                <option value="phone">Phone</option><option value="walk_in">Walk-in</option>
                                <option value="manual">Manual entry</option><option value="website">Website</option>
                            </select>
                        </label>
                        <label className="block"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Rate kind</span>
                            <select className={inputCls} value={form.rate_kind} onChange={(e) => setForm({ ...form, rate_kind: e.target.value })}>
                                <option value="flexible">Flexible</option><option value="best_value">Best Value</option>
                            </select>
                        </label>
                    </div>
                    <label className="block mt-4"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Special requests</span>
                        <textarea rows={2} className={inputCls} value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} />
                    </label>
                    <label className="block mt-4"><span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Initial staff note</span>
                        <input className={inputCls} value={form.staff_note} onChange={(e) => setForm({ ...form, staff_note: e.target.value })} placeholder="e.g. Called to confirm, has infant" />
                    </label>
                </div>

                <button type="submit" disabled={busy} data-testid="create-reservation-submit" className="bg-gold text-navy rounded-full px-8 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-gold/90 inline-flex items-center gap-2 disabled:opacity-60">
                    <Save size={13} /> {busy ? "Creating…" : "Create reservation"}
                </button>
            </form>
        </div>
    );
}
